class MaxUnitsPerOrderController {
  constructor() {
    this.cart = null;
    this.cartRequest = null;
    this.refreshTimer = null;
    this.observer = null;

    this.bindEvents();
    this.refresh(true);
  }

  getCartUrl() {
    return `${window.Shopify?.routes?.root || '/'}cart.js`;
  }

  async fetchCart(force = false) {
    if (!force && this.cart) {
      return this.cart;
    }

    if (this.cartRequest) {
      return this.cartRequest;
    }

    this.cartRequest = fetch(this.getCartUrl(), {
      headers: {
        Accept: 'application/json'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Could not read the current cart.');
        }

        return response.json();
      })
      .then((cart) => {
        this.cart = cart;
        return cart;
      })
      .finally(() => {
        this.cartRequest = null;
      });

    return this.cartRequest;
  }

  invalidateCart() {
    this.cart = null;
  }

  getVariantQuantity(cart, variantId) {
    const numericVariantId = Number(variantId);

    if (!cart || !Array.isArray(cart.items) || !numericVariantId) {
      return 0;
    }

    return cart.items.reduce((total, item) => {
      return Number(item.variant_id) === numericVariantId
        ? total + Number(item.quantity || 0)
        : total;
    }, 0);
  }

  getVariantId(element) {
    const explicitVariantId = Number(element.dataset.maxUnitsVariantId || 0);

    if (explicitVariantId) {
      return explicitVariantId;
    }

    const productForm = element.closest('product-form');
    const form =
      element.closest('form[action*="/cart/add"]') ||
      productForm?.querySelector('form[action*="/cart/add"]');

    const variantInput = form?.querySelector('[name="id"]');

    return Number(variantInput?.value || 0);
  }

  getConfiguredMaximum(element) {
    const elementMaximum = Number(element.dataset.maxUnitsPerOrder || 0);

    if (elementMaximum > 0) {
      return elementMaximum;
    }

    const parent = element.closest('[data-max-units-per-order]');
    const parentMaximum = Number(parent?.dataset.maxUnitsPerOrder || 0);

    return parentMaximum > 0 ? parentMaximum : 0;
  }

  getNativeMaximum(input) {
    if (!input) return null;

    if (!Object.prototype.hasOwnProperty.call(input.dataset, 'maxUnitsNativeMax')) {
      input.dataset.maxUnitsNativeMax = input.hasAttribute('max')
        ? input.getAttribute('max')
        : '';
    }

    const nativeMaximum = Number(input.dataset.maxUnitsNativeMax);

    return nativeMaximum > 0 ? nativeMaximum : null;
  }

  getInput(element) {
    if (element.matches('input[type="number"]')) {
      return element;
    }

    return element.querySelector(
      'input[name="quantity"], input[name="updates[]"], input[type="number"]'
    );
  }

  getSubmitButtons(element) {
    const productForm = element.closest('product-form') || element.querySelector('product-form');

    if (!productForm) return [];

    return Array.from(
      productForm.querySelectorAll(
        'button[type="submit"], button[name="add"], input[type="submit"]'
      )
    );
  }

  getMessageElement(element) {
    const localContainer =
      element.closest('[data-max-units-container]') ||
      element.closest('product-form') ||
      element.parentElement;

    return (
      localContainer?.querySelector('[data-max-units-message]') ||
      element.querySelector?.('[data-max-units-message]') ||
      null
    );
  }

  buildMessage(maximum, currentQuantity, remaining) {
    if (remaining <= 0) {
      return `Maximum ${maximum} per order. You already have the maximum quantity in your cart.`;
    }

    if (currentQuantity > 0) {
      return `Maximum ${maximum} per order. You already have ${currentQuantity} in your cart.`;
    }

    return `Maximum ${maximum} per order.`;
  }

  setMessage(element, message, show) {
    const messageElement = this.getMessageElement(element);

    if (!messageElement) return;

    messageElement.textContent = message;
    messageElement.hidden = !show;
  }

  setButtonAvailability(element, disable) {
    this.getSubmitButtons(element).forEach((button) => {
      if (disable) {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-disabled', 'true');
        button.dataset.maxUnitsDisabled = 'true';
      } else if (button.dataset.maxUnitsDisabled === 'true') {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');
        delete button.dataset.maxUnitsDisabled;
      }
    });
  }

  applyAddContext(element, cart) {
    const maximum = this.getConfiguredMaximum(element);
    const variantId = this.getVariantId(element);

    if (!maximum || !variantId) return;

    const currentQuantity = this.getVariantQuantity(cart, variantId);
    const remaining = Math.max(0, maximum - currentQuantity);
    const input = this.getInput(element);

    if (input) {
      const nativeMaximum = this.getNativeMaximum(input);
      const effectiveMaximum =
        nativeMaximum !== null
          ? Math.min(nativeMaximum, remaining)
          : remaining;

      input.setAttribute('max', String(effectiveMaximum));

      const minimum = Number(input.dataset.min || input.min || 1);
      const currentInputValue = Number(input.value || minimum);

      if (remaining > 0 && currentInputValue > effectiveMaximum) {
        input.value = Math.max(minimum, effectiveMaximum);
      }

      input.closest('quantity-input')?.validateQtyRules?.();
    }

    this.setButtonAvailability(element, remaining <= 0);

    this.setMessage(
      element,
      this.buildMessage(maximum, currentQuantity, remaining),
      currentQuantity > 0 || remaining <= 0
    );
  }

  applyAbsoluteContext(element, cart) {
    const maximum = this.getConfiguredMaximum(element);
    const variantId = this.getVariantId(element);
    const input = this.getInput(element);

    if (!maximum || !variantId || !input) return;

    const nativeMaximum = this.getNativeMaximum(input);
    const effectiveMaximum =
      nativeMaximum !== null
        ? Math.min(nativeMaximum, maximum)
        : maximum;

    input.setAttribute('max', String(effectiveMaximum));

    const currentQuantity = this.getVariantQuantity(cart, variantId);

    input.dataset.cartQuantity = String(currentQuantity);

    input.closest('quantity-input')?.validateQtyRules?.();

    this.setMessage(
      element,
      this.buildMessage(
        maximum,
        currentQuantity,
        Math.max(0, maximum - currentQuantity)
      ),
      currentQuantity >= maximum
    );
  }

  applyCartContext(element, cart) {
    const maximum = this.getConfiguredMaximum(element);
    const variantId = this.getVariantId(element);
    const input = this.getInput(element);

    if (!maximum || !variantId || !input) return;

    const totalVariantQuantity = this.getVariantQuantity(cart, variantId);
    const lineQuantity = Number(
      input.dataset.lineQuantity ||
      input.getAttribute('value') ||
      input.value ||
      0
    );

    const quantityOnOtherLines = Math.max(
      0,
      totalVariantQuantity - lineQuantity
    );

    let rowMaximum = Math.max(0, maximum - quantityOnOtherLines);

    const nativeMaximum = this.getNativeMaximum(input);

    if (nativeMaximum !== null) {
      rowMaximum = Math.min(rowMaximum, nativeMaximum);
    }

    input.setAttribute('max', String(rowMaximum));
    input.dataset.maxUnitsCalculatedMax = String(rowMaximum);

    input.closest('quantity-input')?.validateQtyRules?.();

    this.setMessage(
      element,
      `Maximum ${maximum} of this SKU per order.`,
      totalVariantQuantity >= maximum
    );
  }

  async apply(root = document, forceCartRefresh = false) {
    let cart;

    try {
      cart = await this.fetchCart(forceCartRefresh);
    } catch (error) {
      console.warn('Maximum units per order: cart could not be read.', error);
      return;
    }

    const elements = [];

    if (root.matches?.('[data-max-units-per-order]')) {
      elements.push(root);
    }

    root
      .querySelectorAll?.('[data-max-units-per-order]')
      .forEach((element) => elements.push(element));

    [...new Set(elements)].forEach((element) => {
      const context = element.dataset.maxUnitsContext || 'add';

      if (context === 'absolute') {
        this.applyAbsoluteContext(element, cart);
      } else if (context === 'cart') {
        this.applyCartContext(element, cart);
      } else {
        this.applyAddContext(element, cart);
      }
    });
  }

  async validateProductForm(form) {
    const owner =
      form.closest('[data-max-units-per-order]') ||
      form.querySelector('[data-max-units-per-order]');

    const maximum = Number(owner?.dataset.maxUnitsPerOrder || 0);

    if (!maximum) {
      return {
        allowed: true
      };
    }

    const formData = new FormData(form);
    const variantId = Number(formData.get('id') || 0);
    const requestedQuantity = Math.max(
      1,
      Number(formData.get('quantity') || 1)
    );

    if (!variantId) {
      return {
        allowed: true
      };
    }

    let cart;

    try {
      cart = await this.fetchCart(true);
    } catch (error) {
      /*
        Do not block the customer because of a temporary storefront request
        failure. The future validation Function will remain the definitive
        server-side protection.
      */
      console.warn('Maximum units per order: pre-submit check failed.', error);

      return {
        allowed: true
      };
    }

    const currentQuantity = this.getVariantQuantity(cart, variantId);
    const remaining = Math.max(0, maximum - currentQuantity);

    if (requestedQuantity > remaining) {
      return {
        allowed: false,
        maximum,
        currentQuantity,
        remaining,
        message:
          remaining > 0
            ? `You can only add ${remaining} more. The maximum allowed is ${maximum} per order.`
            : `You already have the maximum allowed quantity of ${maximum} in your cart.`
      };
    }

    return {
      allowed: true,
      maximum,
      currentQuantity,
      remaining
    };
  }

  scheduleRefresh(forceCartRefresh = true) {
    window.clearTimeout(this.refreshTimer);

    this.refreshTimer = window.setTimeout(() => {
      this.refresh(forceCartRefresh);
    }, 50);
  }

  async refresh(forceCartRefresh = true) {
    if (forceCartRefresh) {
      this.invalidateCart();
    }

    await this.apply(document, forceCartRefresh);
  }

  bindEvents() {
    document.addEventListener('DOMContentLoaded', () => {
      this.apply(document, true);
    });

    [
      'cart-add',
      'cart-update',
      'cart-remove',
      'quantity-update',
      'quick-buy-action-complete',
      'shopify:section:load'
    ].forEach((eventName) => {
      document.addEventListener(eventName, () => {
        this.scheduleRefresh(true);
      });
    });

    /*
      Collection filters, cart sections and quick-add modals replace HTML
      dynamically. Watch for new restricted controls and reapply limits.
    */
    this.observer = new MutationObserver((mutations) => {
      const relevantChange = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some((node) => {
          if (!(node instanceof HTMLElement)) return false;

          return (
            node.matches('[data-max-units-per-order]') ||
            node.querySelector('[data-max-units-per-order]')
          );
        });
      });

      if (relevantChange) {
        this.scheduleRefresh(false);
      }
    });

    const startObserver = () => {
      if (!document.body) return;

      this.observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    };

    if (document.body) {
      startObserver();
    } else {
      document.addEventListener('DOMContentLoaded', startObserver, {
        once: true
      });
    }
  }
}

window.MaxUnitsPerOrder = new MaxUnitsPerOrderController();